import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
} from "@f2e/ui/components/base"
import { Check, Plus, Search, Tags, Trash2 } from "lucide-react"
import type { ClientUserTag } from "@/hooks/api"
import {
  DEFAULT_TAG_COLOR,
  getDefaultTagColor,
  TAG_COLOR_OPTIONS,
} from "./clientUserTagColors"

export interface ClientUserTagManagerDialogProps {
  open: boolean
  tags: ClientUserTag[]
  loading: boolean
  onOpenChange: (open: boolean) => void
  onCreateTag: (name: string, color: string) => Promise<boolean>
  onRequestDeleteTag: (tag: ClientUserTag) => void
}

export const ClientUserTagManagerDialog = ({
  open,
  tags,
  loading,
  onOpenChange,
  onCreateTag,
  onRequestDeleteTag,
}: ClientUserTagManagerDialogProps) => {
  const { t } = useTranslation()
  const [searchQuery, setSearchQuery] = useState("")
  const [newTagName, setNewTagName] = useState("")
  const [newTagColor, setNewTagColor] = useState(DEFAULT_TAG_COLOR)
  const [newTagColorEdited, setNewTagColorEdited] = useState(false)
  const [showCustomTagColor, setShowCustomTagColor] = useState(false)

  const filteredTags = useMemo(() => {
    const query = searchQuery.trim().toLowerCase()
    if (!query) return tags
    return tags.filter((tag) => tag.name.toLowerCase().includes(query))
  }, [tags, searchQuery])

  const resetForm = () => {
    setNewTagName("")
    setNewTagColor(DEFAULT_TAG_COLOR)
    setNewTagColorEdited(false)
    setShowCustomTagColor(false)
    setSearchQuery("")
  }

  const handleOpenChange = (nextOpen: boolean) => {
    onOpenChange(nextOpen)
    if (!nextOpen) resetForm()
  }

  const handleCreateTag = async () => {
    const name = newTagName.trim()
    if (!name) return
    const created = await onCreateTag(
      name,
      newTagColor.trim() || DEFAULT_TAG_COLOR
    )
    if (!created) return
    setNewTagName("")
    setNewTagColor(DEFAULT_TAG_COLOR)
    setNewTagColorEdited(false)
    setShowCustomTagColor(false)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Tags className="h-5 w-5" />
            {t("crm.userManagement.tags.managerTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("crm.userManagement.tags.managerDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-5">
          <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
            <div className="space-y-2">
              <Label htmlFor="client-user-tag-name">
                {t("crm.userManagement.tags.name")}
              </Label>
              <Input
                id="client-user-tag-name"
                value={newTagName}
                onChange={(e) => {
                  const nextName = e.target.value
                  setNewTagName(nextName)
                  if (!newTagColorEdited) {
                    setNewTagColor(getDefaultTagColor(nextName))
                  }
                }}
                placeholder={t("crm.userManagement.tags.namePlaceholder")}
              />
            </div>
            <div className="flex items-end">
              <Button
                type="button"
                onClick={handleCreateTag}
                disabled={!newTagName.trim() || loading}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                {t("crm.userManagement.tags.create")}
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label>{t("crm.userManagement.tags.color")}</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowCustomTagColor((current) => !current)}
                className="h-8 px-2 text-xs"
              >
                {t("crm.userManagement.tags.customColor")}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {TAG_COLOR_OPTIONS.map((color) => {
                const isSelected =
                  newTagColor.toLowerCase() === color.toLowerCase()
                return (
                  <button
                    key={color}
                    type="button"
                    className="flex h-8 w-8 items-center justify-center rounded-full border ring-offset-background transition hover:scale-105 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    style={{ backgroundColor: color }}
                    onClick={() => {
                      setNewTagColor(color)
                      setNewTagColorEdited(true)
                    }}
                    aria-label={t("crm.userManagement.tags.selectColor")}
                    aria-pressed={isSelected}
                  >
                    {isSelected && <Check className="h-4 w-4 text-white" />}
                  </button>
                )
              })}
            </div>
            {showCustomTagColor && (
              <div className="flex items-center gap-3 rounded-md border bg-muted/20 p-3">
                <Input
                  id="client-user-tag-color"
                  type="color"
                  value={newTagColor}
                  onChange={(e) => {
                    setNewTagColor(e.target.value)
                    setNewTagColorEdited(true)
                  }}
                  className="h-9 w-14 p-1"
                />
                <span className="font-mono text-xs text-muted-foreground">
                  {newTagColor}
                </span>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label>{t("crm.userManagement.tags.currentTags")}</Label>
              <span className="text-xs text-muted-foreground">
                {t("crm.userManagement.tags.count", { count: tags.length })}
              </span>
            </div>
            {tags.length > 0 ? (
              <div className="space-y-2">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("crm.userManagement.tags.search")}
                    className="pl-9"
                  />
                </div>
                <div className="max-h-72 overflow-y-auto rounded-md border">
                  {filteredTags.length > 0 ? (
                    filteredTags.map((tag) => (
                      <div
                        key={tag.id}
                        className="flex min-h-11 items-center justify-between gap-3 border-b px-3 py-2 last:border-b-0 hover:bg-muted/40"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <span
                            className="h-3 w-3 shrink-0 rounded-full border"
                            style={{
                              backgroundColor: tag.color || DEFAULT_TAG_COLOR,
                            }}
                          />
                          <span className="truncate text-sm font-medium">
                            {tag.name}
                          </span>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => onRequestDeleteTag(tag)}
                          disabled={loading}
                          aria-label={t("crm.userManagement.tags.delete")}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ))
                  ) : (
                    <div className="p-4 text-sm text-muted-foreground">
                      {t("crm.userManagement.tags.noSearchResults")}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
                {t("crm.userManagement.tags.empty")}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
